import { useEffect, useMemo, useState } from "react";
import { Loader2, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

/**
 * DiggerLifeCycle — compact fleet visualisation of the New → 25 → 50 → 75 → New cycle.
 * Space-efficient: 5 stage badges with fleet counts, plus a per-digger strip below.
 */
export default function DiggerLifeCycle() {
  const [wear, setWear] = useState(null);
  const [busy, setBusy] = useState(true);
  const [cats, setCats] = useState("worst");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/get-wear");
        const arr = Array.isArray(data) ? data : data?.items || data?.results || data?.data || [];
        setWear(arr);
      } catch { setWear([]); }
      setBusy(false);
    })();
  }, []);

  const diggers = useMemo(() => {
    if (!Array.isArray(wear)) return [];
    return wear.map((r) => ({
      id: r.asset_id || r.id,
      name: r.asset_id || r.name || r.asset_name,
      tooth: firstNum(r, ["worst_tooth", "tooth_pct", "tooth", "worst_tooth_percent"]),
      lip: firstNum(r, ["worst_lip_shroud", "lip_shroud", "lip_shroud_pct"]),
      wing: firstNum(r, ["worst_wing_shroud", "wing_shroud", "wing_shroud_pct"]),
    })).filter((d) => d.tooth != null || d.lip != null || d.wing != null);
  }, [wear]);

  const pctForCat = (d) => {
    if (cats === "tooth") return d.tooth;
    if (cats === "lip") return d.lip;
    if (cats === "wing") return d.wing;
    // 'worst' — the minimum non-null value across the three
    const vals = [d.tooth, d.lip, d.wing].filter((v) => v != null);
    return vals.length ? Math.min(...vals) : null;
  };

  const stageCounts = useMemo(() => ({
    new:  diggers.filter((d) => bucket(pctForCat(d)) === "new").length,
    s75:  diggers.filter((d) => bucket(pctForCat(d)) === "75").length,
    s50:  diggers.filter((d) => bucket(pctForCat(d)) === "50").length,
    s25:  diggers.filter((d) => bucket(pctForCat(d)) === "25").length,
    rep:  diggers.filter((d) => bucket(pctForCat(d)) === "replace").length,
  }), [diggers, cats]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="digger-life-cycle">
      {/* Category filter */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[10px] uppercase tracking-widest text-muted font-black">Component</div>
        {[
          { k: "worst", l: "Worst overall" },
          { k: "tooth", l: "Tooth" },
          { k: "lip",   l: "Lip Shroud" },
          { k: "wing",  l: "Wing Shroud" },
        ].map((c) => (
          <button
            key={c.k}
            onClick={() => setCats(c.k)}
            className="pill !text-[10px] !py-1 !px-2.5"
            style={{
              background: cats === c.k ? "var(--primary)" : "transparent",
              color: cats === c.k ? "#0a0a0a" : "var(--fg)",
              border: `1px solid ${cats === c.k ? "var(--primary)" : "var(--border)"}`,
              fontWeight: 700,
            }}
            data-testid={`lc-cat-${c.k}`}
          >
            {c.l}
          </button>
        ))}
        <div className="flex-1" />
        <div className="text-[10px] uppercase tracking-widest text-muted">{diggers.length} digger{diggers.length === 1 ? "" : "s"}</div>
      </div>

      {/* Compact stage strip */}
      <div className="grid grid-cols-5 gap-2" data-testid="lc-stages">
        <Stage label="NEW"     range="100%"   count={stageCounts.new}  tone="new" />
        <Stage label="Fresh"   range="≥75%"   count={stageCounts.s75}  tone="good" arrow />
        <Stage label="Mid"     range="≥50%"   count={stageCounts.s50}  tone="watch" arrow />
        <Stage label="Wearing" range="≥25%"   count={stageCounts.s25}  tone="watchHigh" arrow />
        <Stage label="Replace" range="<25%"   count={stageCounts.rep}  tone="replace" arrow />
      </div>

      {busy && !wear ? (
        <div className="panel-flat p-6 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading wear…
        </div>
      ) : diggers.length === 0 ? (
        <div className="panel-flat p-6 text-center text-muted text-sm">
          No digger wear data yet. Log a bucket inspection to populate.
        </div>
      ) : (
        /* Per-digger position bars — one line each */
        <div className="grid gap-1.5" data-testid="lc-diggers">
          {diggers.slice(0, 10).map((d) => {
            const p = pctForCat(d);
            const t = pctTone(p);
            return (
              <div key={d.id} className="grid grid-cols-12 items-center gap-2 text-xs">
                <div className="col-span-3 font-mono-tight truncate">{d.id}</div>
                <div className="col-span-8 relative h-2.5 rounded-full overflow-hidden"
                     style={{ background: "linear-gradient(90deg,#5a0f0f 0%,#5a0f0f 25%,#4a2a06 25%,#4a2a06 50%,#1a2a55 50%,#1a2a55 75%,#0a2a12 75%,#0a2a12 100%)", border: "1px solid var(--border)" }}>
                  {/* Marker */}
                  {p != null && (
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-2 h-4 rounded-sm"
                      style={{
                        left: `calc(${Math.max(0, Math.min(100, p))}% - 4px)`,
                        background: t.color,
                        boxShadow: `0 0 8px ${t.color}`,
                      }}
                      title={`${Math.round(p)}%`}
                    />
                  )}
                </div>
                <div className="col-span-1 text-right font-display font-black tabular-nums" style={{ color: t.color }}>
                  {p == null ? "—" : `${Math.round(p)}%`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stage({ label, range, count, tone, arrow }) {
  const t = TONE[tone] || TONE.good;
  return (
    <div className="relative panel-flat p-3 text-center" style={{ border: `1px solid ${t.border}` }}>
      {arrow && (
        <ArrowRight className="w-3.5 h-3.5 absolute -left-3.5 top-1/2 -translate-y-1/2 text-muted" />
      )}
      <div className="text-[9px] uppercase tracking-widest font-black" style={{ color: t.color }}>
        {tone === "new" && <Sparkles className="w-3 h-3 inline mr-1" />}
        {label}
      </div>
      <div className="font-display font-black text-2xl tabular-nums leading-none mt-1" style={{ color: t.color }}>
        {count}
      </div>
      <div className="text-[10px] text-muted mt-0.5">{range}</div>
    </div>
  );
}

const TONE = {
  new:        { color: "#4ade80", border: "#1e6b32" },
  good:       { color: "#8bb4ff", border: "#3b60d1" },
  watch:      { color: "#ffb347", border: "#f58220" },
  watchHigh:  { color: "#f5a020", border: "#f58220" },
  replace:    { color: "#ff9b9b", border: "#a51919" },
};

function bucket(v) {
  if (v == null || isNaN(v)) return null;
  if (v >= 100) return "new";
  if (v >= 75) return "75";
  if (v >= 50) return "50";
  if (v >= 25) return "25";
  return "replace";
}
function pctTone(v) {
  const b = bucket(v);
  return b === "new" ? TONE.new : b === "75" ? TONE.good : b === "50" ? TONE.watch : b === "25" ? TONE.watchHigh : TONE.replace;
}
function firstNum(o, keys) {
  for (const k of keys) {
    const v = o?.[k];
    if (v !== undefined && v !== null && v !== "") {
      const n = Number(v);
      if (!isNaN(n)) return n <= 1 ? n * 100 : n;
    }
  }
  return null;
}
